/// <amd-module name="Wit/UI/ItemProviders/PagedWorkItemsTreeProvider" />
import { IReadonlyObservableValue, ObservableLike, ObservableValue } from "VSS/Core/Observable";
import { ITreeItemEx, ITreeItem, TreeItemProvider } from "VSSUI/Utilities/TreeItemProvider";

import { WorkItem } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { WorkItemId } from "Wit/UI/Client";
import { IPagedWorkItemsList, IWorkItemTrackingDataProvider } from "Wit/UI/Data";

export class PagedWorkItemsTreeProvider<T> extends TreeItemProvider<T | IReadonlyObservableValue<T | undefined>> {

    private pagedWorkItemsList: IPagedWorkItemsList;
    private dataProvider: IWorkItemTrackingDataProvider;
    private pageSize: number;
    private requestedWorkItemIndexes: Set<number>;
    private toggledTreeNodes: Set<ITreeItem<T>>;
    private mapWorkItems: (workItems: readonly (WorkItem | null)[] | null, options?: { isRootNode?: boolean }) => ITreeItem<T | IReadonlyObservableValue<T | undefined>>[];

    constructor(itemsSource: WorkItemId[] | WorkItem[] | string, mapWorkItems: (workItems: readonly (WorkItem | null)[] | null, options?: { isRootNode?: boolean }) => ITreeItem<T | IReadonlyObservableValue<T | undefined>>[], dataProvider: IWorkItemTrackingDataProvider, pageSize: number) {
        let workItemTreeItems: ITreeItem<T | IReadonlyObservableValue<T | undefined>>[];
        const pagedWorkItemsList = dataProvider.getPagedWorkItemsList(itemsSource, pageSize);
        if (pagedWorkItemsList.isFirstPageReady()) {
            const workItems = pagedWorkItemsList.getAllItems();
            workItemTreeItems = mapWorkItems(workItems, { isRootNode: true });
        } else {
            /** 
             * Initialize empty rows with undefined ObservableValues so they display with a loading shimmer
             * until their work items are available.
             */
            workItemTreeItems = Array.from({ length: itemsSource.length }, () => {
                return {
                    data: new ObservableValue<T | undefined>(undefined)
                }
            });
        }

        super(workItemTreeItems);

        this.mapWorkItems = mapWorkItems;
        this.pagedWorkItemsList = pagedWorkItemsList;
        this.dataProvider = dataProvider;
        this.pageSize = pageSize;
        this.requestedWorkItemIndexes = new Set<number>();
        this.toggledTreeNodes = new Set<ITreeItem<T>>();
    }

    public async ensureFirstPage(): Promise<void> {
        /**
         * mark the first page as being requested to prevent any further
         * requests for these items
         */
        this.updatedRequestedWorkItemIndexes(0);
        await this.pagedWorkItemsList.ensureFirstPage();
        const workItems = this.pagedWorkItemsList.getAllItems();
        const workItemTreeItems = this.mapWorkItems(workItems, { isRootNode: true });

        this.clear();
        this.splice(undefined, undefined, [{ items: workItemTreeItems }]);
    }

    public getItem(index: number): ITreeItemEx<T | IReadonlyObservableValue<T | undefined>> {
        /**
         * The item is observable if it's not loaded. Once loaded, it's no longer an observable.
         * Since getItem is called for every single row, we do not want to call debouncedLoadItemPage per row. 
         */
        if (this.value[index] && this.value[index].underlyingItem && ObservableLike.isObservable(this.value[index].underlyingItem.data)) {
            this.loadItemPage(index);
        }

        return this.value[index];
    }

    /**
     * When a tree node is toggled, fetch the node's child work items (upto one page) if present and update the tree.
     */
    public onToggle(treeItemEx: ITreeItemEx<T>, childrenWorkItemIds: number[]): void {
        if (childrenWorkItemIds) {
            const treeItem = treeItemEx.underlyingItem;
            if (!this.toggledTreeNodes.has(treeItem)) {
                const pagedChildrenList = this.dataProvider.getPagedWorkItemsList(childrenWorkItemIds, this.pageSize);
                pagedChildrenList.ensureFirstPage().then(() => {
                    if (pagedChildrenList.isFirstPageReady()) {
                        const childWorkItems = pagedChildrenList.getAllItems();
                        const childWorkItemsFirstPage = childWorkItems && childWorkItems.slice(0, this.pageSize);

                        if (childWorkItemsFirstPage != null) {
                            // Create tree nodes for each child.
                            const treeChildItems = this.mapWorkItems(childWorkItemsFirstPage);

                            // Update the tree with the new child nodes.
                            this.splice(
                                treeItem /* parent item */,
                                treeItem.childItems /* items to remove */,
                                [{ items: treeChildItems }] /* items to add */
                            );

                            this.toggledTreeNodes.add(treeItem);
                        }
                    }
                });
            }

            this.toggle(treeItem);
        }
    }

    public isFirstPageReady(): boolean {
        return this.pagedWorkItemsList.isFirstPageReady();
    }

    private async loadItemPage(itemIndex: number): Promise<void> {
        // Item has already been requested, so return.
        if (this.requestedWorkItemIndexes.has(itemIndex)) {
            return;
        }

        /**
         *  Filter the list of all items to only the items we want to replace, i.e. the page to be loaded. 
         *  If a user randomly scrolls to an index, loadItemPage returns the page to which the index belongs. 
         *  For example, if the pageSize is 10 and the index is 12, we get items from index 10-19. We update the
         *  indexes here accordingly.
         **/
        const pageStart = itemIndex - (itemIndex % this.pageSize);
        const pageEnd = pageStart + this.pageSize;

        this.updatedRequestedWorkItemIndexes(pageStart);
        await this.pagedWorkItemsList.loadItemPage(itemIndex);
        const allWorkItems = this.pagedWorkItemsList.getAllItems();

        if (allWorkItems) {
            const updatedWorkItems = allWorkItems.slice(pageStart, pageEnd);
            const updatedWorkItemTreeItems = this.mapWorkItems(updatedWorkItems, { isRootNode: true });

            // Get the list of placeholder items in the tree that have to be replaced.
            const indexesToReplace = Object.keys(this.value).filter((value: string, index: number) => { return (index >= pageStart && index < pageEnd); });
            const itemsToReplace = indexesToReplace.map((requestedIndex: string) => { return this.value[parseInt(requestedIndex)].underlyingItem; })

            // Update the next page with fetched items.
            const treeItemAdd = {
                items: updatedWorkItemTreeItems,
                insertAfter: this.value[pageStart - 1] && this.value[pageStart - 1].underlyingItem
            };
                
            this.splice(undefined, itemsToReplace, [treeItemAdd]);
        }
    }

    private updatedRequestedWorkItemIndexes(index: number): void {
        for (let i = index; i < index + this.pageSize; i++) {
            this.requestedWorkItemIndexes.add(i);
        }
    }
}